import type { ThemeLanguageProps } from "@/types/layout-type";
import { ChevronDown, Globe, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

export default function ThemeAndLanguage({
    languages,
    isDark,
    toggleTheme,
    setScrolled,
    changeLanguage,
}: ThemeLanguageProps) {
    const { lang } = useParams();
    const currentLangLabel =
        languages.find((l) => l.code === lang)?.code.toUpperCase() || "ID";
    const [langDropdown, setLangDropdown] = useState(false);
    const langRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                langRef.current &&
                !langRef.current.contains(event.target as Node)
            ) {
                setLangDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="hidden md:flex items-center gap-4 pr-4 border-r border-gray-200 dark:border-dark-2">
            <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-dark transition-colors text-gray-600 dark:text-gray-300 hover:text-gold dark:hover:text-gold cursor-pointer"
                title={isDark ? "Mode Terang" : "Mode Gelap"}>
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className="relative" ref={langRef}>
                <button
                    onClick={() => setLangDropdown(!langDropdown)}
                    className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark transition-colors text-gray-600 hover:text-gold dark:hover:text-gold cursor-pointer dark:text-gray-300 font-medium">
                    <Globe size={20} />
                    <span className="text-sm">{currentLangLabel}</span>
                    <ChevronDown
                        size={14}
                        className={`transform transition-transform ${langDropdown ? "rotate-180" : ""}`}
                    />
                </button>
                {langDropdown && (
                    <div className="absolute z-60 top-full right-0 mt-2 w-40 bg-white dark:bg-dark border border-gray-100 dark:border-dark-2 rounded-xl shadow-2xl overflow-hidden py-2">
                        {languages.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => {
                                    changeLanguage(l.code);
                                    setLangDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 hover:bg-surface dark:hover:bg-gold/20 transition-colors text-sm ${lang === l.code ? "text-gold font-bold bg-surface/50 dark:bg-gold/20" : "text-dark dark:text-white cursor-pointer"}`}>
                                {l.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
